import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Group,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Checkbox,
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { ActionButton, AppModal } from '@/components/ui'
import { api, ApiError, DEFAULT_ERROR_NOTIFY_MESSAGE } from '@/lib/api'
import { downloadBlob } from '@/lib/download-blob'
import type { AIKnowledgeBaseRow } from '@/types/api'

type Props = {
  projectId: string
  projectAiId: string
  canEdit: boolean
  refreshKey: number
}

export function AiKnowledgeTab({ projectId, projectAiId, canEdit, refreshKey }: Props) {
  const [rows, setRows] = useState<AIKnowledgeBaseRow[]>([])
  const [loading, setLoading] = useState(true)
  const fileRef = useRef<HTMLInputElement>(null)
  const [modal, setModal] = useState<AIKnowledgeBaseRow | 'new' | null>(null)
  const [saving, setSaving] = useState(false)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState('')
  const [url, setUrl] = useState('')
  const [category, setCategory] = useState('')
  const [active, setActive] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const list = await api.knowledgeBaseList(projectId)
      setRows(Array.isArray(list) ? list : [])
    } catch {
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  const openNew = () => {
    setModal('new')
    setTitle('')
    setContent('')
    setQuestion('')
    setAnswer('')
    setUrl('')
    setCategory('')
    setActive(true)
  }

  const openEdit = (item: AIKnowledgeBaseRow) => {
    setModal(item)
    setTitle(item.title)
    setContent(item.content || '')
    setQuestion(item.question || '')
    setAnswer(item.answer || '')
    setUrl(item.url || '')
    setCategory(item.category || '')
    setActive(item.is_active !== false)
  }

  const saveKb = async () => {
    const t = title.trim()
    if (!t) {
      showNotification({ title: 'Форма', message: 'Название обязательно', color: 'yellow' })
      return
    }
    const hasQa = question.trim() && answer.trim()
    const hasUrl = url.trim()
    const resolvedType = hasQa ? 'qa' : hasUrl ? 'url' : 'text'
    setSaving(true)
    try {
      if (modal === 'new') {
        await api.knowledgeBaseCreate(projectId, {
          project_ai_id: projectAiId,
          content_type: resolvedType,
          title: t,
          content: resolvedType === 'text' ? content : null,
          question: resolvedType === 'qa' ? question : null,
          answer: resolvedType === 'qa' ? answer : null,
          url: resolvedType === 'url' ? url : null,
          category: category.trim() || null,
          is_active: active,
        })
      } else if (modal) {
        await api.knowledgeBaseUpdate(projectId, modal.id, {
          title: t,
          content: resolvedType === 'text' ? content : null,
          question: resolvedType === 'qa' ? question : null,
          answer: resolvedType === 'qa' ? answer : null,
          url: resolvedType === 'url' ? url : null,
          category: category.trim() || null,
          is_active: active,
        })
      }
      showNotification({ title: 'Сохранено', message: '', color: 'green' })
      setModal(null)
      await load()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось сохранить',
        color: 'red',
      })
    } finally {
      setSaving(false)
    }
  }

  const exportExcel = async () => {
    try {
      const blob = await api.exportKnowledgeBaseExcel(projectId)
      downloadBlob(blob, `knowledge-${projectId}.xlsx`)
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
        color: 'red',
      })
    }
  }

  return (
    <Stack gap="md">
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (!f || !canEdit) return
          void api
            .uploadKnowledgeBaseExcel(projectId, f)
            .then((res) => {
              showNotification({
                title: 'Импорт',
                message: (res as { message?: string })?.message || 'Готово',
                color: 'green',
              })
              return load()
            })
            .catch((err) =>
              showNotification({
                title: 'Ошибка',
                message: err instanceof ApiError ? err.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
                color: 'red',
              }),
            )
            .finally(() => {
              if (fileRef.current) fileRef.current.value = ''
            })
        }}
      />
      <Group gap="xs" wrap="wrap">
        {canEdit ? <Button size="sm" onClick={openNew}>Добавить элемент</Button> : null}
        <Button size="sm" variant="default" onClick={() => void exportExcel()}>
          Выгрузить Excel
        </Button>
        {canEdit ? (
          <Button size="sm" variant="light" onClick={() => fileRef.current?.click()}>
            Загрузить Excel
          </Button>
        ) : null}
        {canEdit ? (
          <Button
            size="sm"
            color="red"
            variant="light"
            onClick={() =>
              openConfirmModal({
                title: 'Удалить всю базу знаний?',
                confirmProps: { color: 'red' },
                onConfirm: () =>
                  void api
                    .knowledgeBaseDeleteAll(projectId)
                    .then(() => load())
                    .then(() => showNotification({ title: 'Удалено', message: '', color: 'green' }))
                    .catch((e) =>
                      showNotification({ title: 'Ошибка', message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE, color: 'red' }),
                    ),
              })
            }
          >
            Удалить всё
          </Button>
        ) : null}
      </Group>

      {loading ? (
        <Text size="sm">Загрузка…</Text>
      ) : rows.length === 0 ? (
        <Text size="sm" c="dimmed">
          База знаний пуста
        </Text>
      ) : (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Название</Table.Th>
              <Table.Th>Тип</Table.Th>
              <Table.Th>Категория</Table.Th>
              <Table.Th>Акт.</Table.Th>
              <Table.Th w={160} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((r) => (
              <Table.Tr key={r.id}>
                <Table.Td>{r.title}</Table.Td>
                <Table.Td>{r.content_type}</Table.Td>
                <Table.Td>{r.category || '—'}</Table.Td>
                <Table.Td>{r.is_active !== false ? 'да' : 'нет'}</Table.Td>
                <Table.Td>
                  <Group gap={6}>
                    <Button size="compact-xs" variant="default" onClick={() => openEdit(r)}>
                      Изменить
                    </Button>
                    {canEdit ? (
                      <Button
                        size="compact-xs"
                        color="red"
                        variant="light"
                        onClick={() =>
                          openConfirmModal({
                            title: 'Удалить элемент?',
                            onConfirm: () =>
                              void api
                                .knowledgeBaseDelete(projectId, r.id)
                                .then(() => load())
                                .then(() => showNotification({ title: 'Удалено', message: '', color: 'green' }))
                                .catch((e) =>
                                  showNotification({
                                    title: 'Ошибка',
                                    message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
                                    color: 'red',
                                  }),
                                ),
                          })
                        }
                      >
                        Удалить
                      </Button>
                    ) : null}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <AppModal
        opened={modal != null}
        onClose={() => setModal(null)}
        title="База знаний"
        size="lg"
        footer={
          <>
            <ActionButton action="cancel" onClick={() => setModal(null)} />
            <ActionButton action="save" loading={saving} onClick={() => void saveKb()} />
          </>
        }
      >
        <Stack gap="sm">
          <TextInput label="Название" required value={title} onChange={(e) => setTitle(e.target.value)} />
          <Text size="xs" c="dimmed">
            Тип определяется автоматически: если заполнены «Вопрос» и «Ответ» → QA, если заполнен URL → URL, иначе → Текст.
          </Text>
          <Textarea label="Вопрос (для QA)" value={question} onChange={(e) => setQuestion(e.target.value)} minRows={2} />
          <Textarea label="Ответ (для QA)" value={answer} onChange={(e) => setAnswer(e.target.value)} minRows={4} />
          <TextInput label="URL" value={url} onChange={(e) => setUrl(e.target.value)} />
          <Textarea label="Содержимое (текст)" value={content} onChange={(e) => setContent(e.target.value)} minRows={6} />
          <TextInput label="Категория" value={category} onChange={(e) => setCategory(e.target.value)} />
          <Checkbox label="Активен" checked={active} onChange={(e) => setActive(e.currentTarget.checked)} />
        </Stack>
      </AppModal>
    </Stack>
  )
}
